<template>
  <form
    v-focus-outside="searchFormUnfocus"
    class="advanced-search"
  >
    <ul>
      <li class="adv-search-block">
        <h2 class="options-list-container-title">
          {{ $t("recherche.recherche-avancee") }}
        </h2>
      </li>
      <li class="adv-search-block">
        <label class="text-medium">
          {{ $t("recherche.rechercher-dans") }}
        </label>
        <SelectClassic
          v-model="currentCatalogue"
          status="required"
          class="search-param"
          :options="searchCatalogues"
          :disabled="isCatalogueUnique"
          inline
          select-on-enter
          @update:modelValue="clearSearch()"
        />
      </li>
      <li
        v-for="(currentSearchOption, index) in currentSearch"
        :key="currentSearchOption.id"
        class="adv-search-block text-medium"
      >
        <label class="text-medium">
          {{ $t("recherche.et-par") }}
        </label>
        <SelectClassic
          v-model="currentSearchOption.param"
          name="searchParams"
          class="search-param"
          :options="availableOptions"
          select-on-enter
          inline
          @update:modelValue="addLine(index)"
        />

        <SelectClassic
          v-if="currentSearchOption.param?.choices"
          v-model="currentSearchOption.term"
          class="search-term"
          :options="currentSearchOption.param?.choices"
          select-on-enter
          inline
        />
        <InputClassic
          v-else-if="currentSearchOption.param?.value !== 'prix'"
          v-model="currentSearchOption.term"
          class="search-term"
          variant="solid"
          inline
          :placeholder="$t('recherche.renseigner')"
          @keyup.enter="submitForm()"
        />
        <div
          v-else-if="currentSearchOption.param?.value === 'prix'"
          class="search-prix"
        >
          {{ $t("recherche.a-partir-de") }}
          <InputQuantity
            v-model="currentSearchOption.from"
            class="search-term"
            variant="solid"
            inline
            hide-buttons
            :min="0"
            :max="100000"
            :placeholder="$t('recherche.somme-minimum')"
            @keyup.enter="submitForm()"
          />
          {{ $t("prix.jusqua") }}
          <InputQuantity
            v-model="currentSearchOption.to"
            class="search-term"
            variant="solid"
            inline
            hide-buttons
            :min="1"
            :max="100000"
            :placeholder="$t('recherche.somme-maximum')"
            @keyup.enter="submitForm()"
          />
          €
        </div>

        <ButtonClassic
          v-if="currentSearchOption.param"
          variant="solid"
          color="primary"
          icon="left"
          size="small"
          @click="deleteLine(index)"
        >
          <template #left-icon>
            <UilTrashAlt />
          </template>
        </ButtonClassic>
      </li>
      <li class="adv-search-block">
        <ButtonClassic
          class="adv-search-button"
          color="primary"
          variant="solid"
          icon="left"
          :label="$t('recherche.lancer-la-recherche')"
          :disabled="!canSearch"
          @click="submitForm()"
        >
          <template #left-icon="{ iconSize, }">
            <UilSearchAlt :size="iconSize" />
          </template>
        </ButtonClassic>
      </li>
    </ul>
  </form>
</template>

<script>
import { mapGetters } from "vuex";

import {
  ButtonClassic,
  SelectClassic,
  InputClassic,
  InputQuantity,
} from "@lde/core_lde_vue";
import { UilTrashAlt, UilSearchAlt } from "@lde/vue-unicons";

import Api from "@/modules/axios";

export default {
  name: "AdvancedSearch",
  components: {
    SelectClassic,
    InputClassic,
    InputQuantity,
    ButtonClassic,
    UilSearchAlt,
    UilTrashAlt,
  },
  props: {
    /**
     * Catalogue sélectionnée dans la barre de recherche rapide.
     */
    quickSearchCatalogue: {
      type: Object,
      default: null,
    },
  },
  emits: [
    /**
     * Déclenché au lancement de la recherche.
     */
    "search",
  ],
  data() {
    return {
      currentSearch: [],
      currentCatalogue: {},
      choices: {},
      searchObject: {
        param: null,
        term: null,
      },
    };
  },
  computed: {
    ...mapGetters(["hasPerm", "advancedSearchActive", "organismeActuel"]),
    canSearch() {
      return this.currentSearch.every((choice) => {
        if (choice.param && (choice.term || (choice.from && choice.to))) {
          return true;
        }
        if (!choice.param && this.currentSearch.length > 1) {
          return true;
        }
        return false;
      });
    },
    searchCatalogues() {
      const cat = [];
      const appendCatalogue = (catalogue) => {
        if (!cat.some((existing) => existing.value === catalogue.value)) {
          cat.push(catalogue);
        }
      };
      const activeCatalogues = this.organismeActuel?.catalogues_actifs || [];

      activeCatalogues.forEach((catalogue) => {
        const formatted = this.formatSearchCatalogue(catalogue);
        if (formatted) {
          appendCatalogue(formatted);
        }
      });

      // Fallback legacy (3 catalogues historiques)
      if (!cat.length) {
        if (this.hasPerm("view_articlepapier")) {
          appendCatalogue({
            label: this.$t("menu.livres-et-manuels-papier"),
            value: "papiers",
            baseTab: "papiers",
          });
        }
        if (this.hasPerm("view_manuelnumerique")) {
          appendCatalogue({
            label: this.$t("menu.ressources-numeriques"),
            value: "numeriques",
            baseTab: "numeriques",
          });
        }
        if (this.hasPerm("view_fourniture")) {
          appendCatalogue({
            label: this.$t("menu.fournitures-et-papeterie"),
            value: "fournitures",
            baseTab: "fournitures",
          });
        }
      }

      return cat;
    },
    /**
     * Un seul catalogue possible : inutile de laisser le choix.
     */
    isCatalogueUnique() {
      return this.searchCatalogues.length === 1;
    },
    advancedSearchOptions() {
      const currentBaseTab = this.currentCatalogue?.baseTab || this.currentCatalogue?.value;
      const options = {
        papiers: [
          { label: this.$t("general.titre"), value: "libelle" },
          { label: this.$t("produit.auteur"), value: "auteurs" },
          { label: this.$t("produit.editeur"), value: "editeur" },
          { label: this.$t("produit.langue"), value: "langue", choices: this.choices.langues_pap },
          { label: this.$t("produit.theme"), value: "themes", choices: this.choices.themes_pap },
          { label: this.$t("produit.annee-edition"), value: "date_publication", choices: this.choices.annee_pap },
          { label: this.$t("produit.ean"), value: "ean" },
          { label: this.$t("prix.prix"), value: "prix" },
        ],
        numeriques: [
          { label: this.$t("general.titre"), value: "libelle" },
          { label: this.$t("produit.editeur"), value: "editeur" },
          { label: this.$t("general.type-etablissement"), value: "niveaux", choices: this.choices.niveaux_num },
          { label: this.$t("produit.discipline"), value: "matieres", choices: this.choices.matieres_num },
          { label: this.$t("produit.classe"), value: "classes", choices: this.choices.classes_num },
          { label: this.$t("produit.annee-edition"), value: "date_publication", choices: this.choices.annee_num },
          { label: this.$t("produit.ean"), value: "ean" },
          { label: this.$t("produit.code-ark"), value: "code_ark" },
          { label: this.$t("prix.prix"), value: "prix" },
          { label: this.$t("filtre.nouveautes"), value: "nouveaute", choices: this.choices.nouveaute_num },
        ],
        fournitures: [
          { label: this.$t("produit.marque"), value: "marque" },
          { label: this.$t("general.designation"), value: "libelle" },
          { label: this.$t("produit.couleur"), value: "coloris" },
        ],
      };

      return currentBaseTab ? options[currentBaseTab] : [];
    },
    availableOptions() {
      return this.advancedSearchOptions.filter((option) => {
        const isOptionChosen = this.currentSearch.some(
          (currentOption) => currentOption?.param?.label === option.label,
        );
        return !isOptionChosen;
      });
    },
  },
  watch: {
    searchCatalogues: {
      // Sélectionne d'office le catalogue unique, sans attendre le chargement des choix de recherche.
      handler(catalogues) {
        if (catalogues.length === 1) {
          [this.currentCatalogue] = catalogues;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.currentSearch.push({ ...this.searchObject, id: Date.now() });
    this.getAdvancedOptions();
  },
  methods: {
    /**
     * Met en place les données pour les choix de la recherche avancée.
     */
    getAdvancedOptions() {
      Api().get("/recherche/advanced_choices/")
        .then(({ data }) => {
          this.choices = data;
          this.setSearch();
        })
        .catch((err) => {
          console.error(err);
        });
    },
    /**
     * Remet en place la recherche précédente si dans l'url le parametre "advanced" est true.
     */
    setSearch() {
      const query = this.$route.query;
      const adv = query.advanced;
      const cat = query.only;

      if (adv) {
        this.currentCatalogue = this.findCatalogueByValue(cat) || this.searchCatalogues[0];
        this.currentSearch = [];

        Object.entries(query).forEach(([param, term]) => {
          if (param.includes("_adv_")) {
            const opt = param.replace(`${query.only}_adv_`, "");
            const searchOption = {
              param: this.advancedSearchOptions.find((obj) => obj.value === opt),
            };

            if (searchOption.param.choices) {
              // Il y a parfois un {Objet} choices et pas simplement du texte.
              searchOption.term = searchOption.param.choices.find((obj) => obj.value === term);
            } else if (searchOption.param?.value === "prix") {
              [searchOption.from, searchOption.to] = term.split("-");
              searchOption.from = parseInt(searchOption.from, 10);
              searchOption.to = parseInt(searchOption.to, 10);
            } else {
              searchOption.term = term;
            }
            this.currentSearch.push(searchOption);
          }
        });

        this.currentSearch.push({ ...this.searchObject, id: Date.now() });
      } else {
        const quickCat = this.findCatalogueByValue(this.quickSearchCatalogue?.name);
        this.currentCatalogue = quickCat || this.searchCatalogues[0];
      }
    },
    findCatalogueByValue(rawValue) {
      if (!rawValue || typeof rawValue !== "string") {
        return null;
      }

      // 1) Match exact (prioritaire pour les valeurs dynamiques baseTab__slug).
      const exactMatch = this.searchCatalogues.find((obj) => obj.value === rawValue);
      if (exactMatch) {
        return exactMatch;
      }

      // 2) Fallback legacy: query.only peut contenir uniquement la base (ex: "papiers").
      // On évite includes(...) trop permissif et on matche explicitement sur baseTab.
      return this.searchCatalogues.find((obj) => obj.baseTab === rawValue) || null;
    },
    /**
     * Met une ligne vide à la fin de la recherche avancée.
     * @param {Integer} index Position du tableau à réinitialiser.
     */
    addLine(index) {
      const emptyLines = this.currentSearch.some((choice) => !choice.param);
      if (!emptyLines && this.currentSearch.length < this.advancedSearchOptions.length) {
        this.currentSearch.push({ ...this.searchObject, id: Date.now() });
      }
      this.currentSearch[index].term = null;
    },
    /**
     * Supprime une ligne de la recherche avancée.
     * @param {Integer} index Position du tableau à supprimer.
     */
    deleteLine(index) {
      const emptyLines = this.currentSearch.some((choice) => !choice.param);
      if (this.currentSearch.length === 1) {
        this.currentSearch = [{ ...this.searchObject, id: Date.now() }];
      } else if (!emptyLines && this.currentSearch.length === this.advancedSearchOptions.length) {
        this.currentSearch.splice(index, 1, { ...this.searchObject, id: Date.now() });
      } else {
        this.currentSearch.splice(index, 1);
      }
    },
    /**
     * Vide la recherche avancée en cours.
     */
    clearSearch() {
      this.currentSearch = [{ ...this.searchObject, id: Date.now() }];
    },
    /**
     * Soumet la recherche avancée en cours et pousse vers la vue de recherche.
     */
    async submitForm() {
      await this.$nextTick();

      if (this.canSearch) {
        const query = {
          advanced: true,
          only: this.currentCatalogue.value,
        };
        this.currentSearch.forEach((choice) => {
          if (choice.param && choice.term) {
            const param = `${this.currentCatalogue.value}_adv_${choice.param.value}`;
            query[param] = choice.term?.value || choice.term;
          } else if (choice.param && choice.from && choice.to) {
            const param = `${this.currentCatalogue.value}_adv_${choice.param.value}`;
            query[param] = `${parseInt(choice.from, 10)}-${parseInt(choice.to, 10)}`;
          }
        });
        await this.$router
          .push({
            name: "search",
            query: {
              ...query,
            },
          });
        this.$emit("search");
        if (this.$route.name === "search") {
          // On rafraîchit la page
          this.$store.commit("addSearchKey");
        }
      }
    },
    /**
     * Ferme le formulaire de recherche avancée si on clique en dehors du dropdown.
     * On doit exclure le bouton (open_adv_search) sinon on réouvre à nouveau le menu.
     */
    searchFormUnfocus(event) {
      if (event.target.id !== "open_adv_search") {
        this.$store.dispatch("toggleAdvSearch");
      }
    },
    formatSearchCatalogue(catalogue) {
      const typeToConfig = {
        papier: {
          perm: "view_articlepapier",
          baseTab: "papiers",
          fallbackLabel: this.$t("menu.livres-et-manuels-papier"),
        },
        numerique: {
          perm: "view_manuelnumerique",
          baseTab: "numeriques",
          fallbackLabel: this.$t("menu.ressources-numeriques"),
        },
        fourniture: {
          perm: "view_fourniture",
          baseTab: "fournitures",
          fallbackLabel: this.$t("menu.fournitures-et-papeterie"),
        },
      };

      const config = typeToConfig[catalogue?.type];
      if (!config || !this.hasPerm(config.perm)) {
        return null;
      }

      const slug = catalogue?.slug || "";
      return {
        label: catalogue?.nom || config.fallbackLabel,
        value: slug ? `${config.baseTab}__${slug}` : config.baseTab,
        baseTab: config.baseTab,
      };
    },
  },
};
</script>

<style lang="scss">
@use "@/assets/styles/components/search/advanced_search.scss";
</style>
